/**
 * syncLog.ts — a local record of what sync did, for the sync hub.
 *
 * Holds ids, counts and errors only, never text or titles, and never leaves
 * the device. The same event repeated within a minute (another batch of
 * keystrokes reaching the cloud, another empty check for changes) is merged
 * into one line, so a long writing session reads as a few lines rather than
 * thousands. Lines are written to IndexedDB in small batches and kept for
 * KEEP_DAYS, up to MAX_ENTRIES.
 */

import { shallowRef } from 'vue'
import { db, type StoredSyncLogEntry } from '../platform/webStorage'

export type SyncLogEntry = StoredSyncLogEntry

interface SyncLogEvent {
  direction: SyncLogEntry['direction']
  level?: SyncLogEntry['level']
  kind: string
  message: string
  targetType?: SyncLogEntry['targetType']
  targetIds?: string[]
  items?: number
  httpStatus?: number
}

const MERGE_WINDOW_MS = 60_000
const PERSIST_DELAY_MS = 2_000
const MAX_TARGET_IDS = 50
export const MAX_ENTRIES = 1_000
export const KEEP_DAYS = 14
const RECENT_LIMIT = 200

function mergeKey(event: SyncLogEvent): string {
  return [event.direction, event.level ?? 'info', event.kind, event.message, (event.targetIds ?? []).join(',')].join('|')
}

class SyncLog {
  /** Newest first; the lines written this session plus those loaded. */
  public readonly recent = shallowRef<SyncLogEntry[]>([])

  private lastByKey = new Map<string, SyncLogEntry>()
  private dirty = new Map<string, SyncLogEntry>()
  private persistTimer: ReturnType<typeof setTimeout> | null = null
  private writesSincePrune = 0

  public record(event: SyncLogEvent, now = Date.now()): SyncLogEntry {
    const key = mergeKey(event)
    const previous = this.lastByKey.get(key)
    if (previous && now - previous.at <= MERGE_WINDOW_MS) {
      previous.at = now
      previous.repeats += 1
      if (event.items !== undefined) previous.items = (previous.items ?? 0) + event.items
      this.markDirty(previous)
      // Move it back to the top.
      this.recent.value = [previous, ...this.recent.value.filter((entry) => entry !== previous)]
      return previous
    }

    const entry: SyncLogEntry = {
      id: `${now.toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
      at: now,
      firstAt: now,
      direction: event.direction,
      level: event.level ?? 'info',
      kind: event.kind,
      message: event.message,
      repeats: 1,
    }
    if (event.targetType) entry.targetType = event.targetType
    if (event.targetIds?.length) entry.targetIds = event.targetIds.slice(0, MAX_TARGET_IDS)
    if (event.items !== undefined) entry.items = event.items
    if (event.httpStatus !== undefined) entry.httpStatus = event.httpStatus

    this.lastByKey.set(key, entry)
    this.markDirty(entry)
    this.recent.value = [entry, ...this.recent.value].slice(0, RECENT_LIMIT)
    return entry
  }

  /** Reads the stored log, newest first, and merges in unsaved lines. */
  public async load(limit = MAX_ENTRIES): Promise<SyncLogEntry[]> {
    await this.persist()
    try {
      return await db.syncLog.orderBy('at').reverse().limit(limit).toArray()
    } catch {
      return this.recent.value
    }
  }

  public async persist(): Promise<void> {
    if (this.persistTimer) {
      clearTimeout(this.persistTimer)
      this.persistTimer = null
    }
    if (this.dirty.size === 0) return
    const batch = [...this.dirty.values()].map((entry) => ({ ...entry }))
    this.dirty.clear()
    try {
      await db.syncLog.bulkPut(batch)
      this.writesSincePrune += batch.length
      if (this.writesSincePrune >= 100) {
        this.writesSincePrune = 0
        await this.prune()
      }
    } catch (e) {
      console.warn('[SyncLog] Could not save sync log:', e)
    }
  }

  public async prune(now = Date.now()): Promise<void> {
    try {
      await db.syncLog.where('at').below(now - KEEP_DAYS * 86_400_000).delete()
      const count = await db.syncLog.count()
      if (count > MAX_ENTRIES) {
        const oldest = await db.syncLog.orderBy('at').limit(count - MAX_ENTRIES).primaryKeys()
        await db.syncLog.bulkDelete(oldest)
      }
    } catch (e) {
      console.warn('[SyncLog] Could not prune sync log:', e)
    }
  }

  /** Forgets everything; used by tests and when the log is cleared by the user. */
  public async clear(): Promise<void> {
    if (this.persistTimer) clearTimeout(this.persistTimer)
    this.persistTimer = null
    this.dirty.clear()
    this.lastByKey.clear()
    this.recent.value = []
    try {
      await db.syncLog.clear()
    } catch {}
  }

  private markDirty(entry: SyncLogEntry): void {
    this.dirty.set(entry.id, entry)
    if (!this.persistTimer) {
      this.persistTimer = setTimeout(() => {
        this.persistTimer = null
        void this.persist()
      }, PERSIST_DELAY_MS)
    }
  }
}

export const syncLog = new SyncLog()

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => { void syncLog.persist() })
  void syncLog.prune()
}
